import {
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ApprovalWorkflowItemDto, AssetItemDto, AWAssetDto } from '@api/Api';
import { useTypedSelector } from '@hooks';
import { useGetApprovalWorkflow } from '@hooks/queries/approval';
import {
  ASSETS_SLIDER_PAGE_SIZE,
  fetchAssetsListEdge,
  useAssetsCountQuery,
  useAssetsSliderQuery
} from '@hooks/queries/assets';
import {
  keepAssetsOrder,
  sortedAWListAssetsToReviewInBatch
} from '@components/Modals/ApprovalModal/utils';
import { useShareReview } from '@components/ShareReviewProvider';
import { useApprovalPassword } from '@context/ApprovalPasswordContext';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { nonNull } from '@helpers/non-null';
import {
  setAsset,
  setAssetsSliderAnchor,
  setAssetVariant
} from '@redux/actions/mediaViewerAction';
import { getInitialAssetsOrderByParam } from '@pages/Campaigns/CampaignItem/components/AssetsTab/AssetsTabControls';
import {
  getApprovalMediaViewerUrl,
  useIsApprovalMediaViewerMode
} from '@pages/MediaViewer/helpers/mediaViewerMode';

export function isApprovalAsset(
  asset: AWAssetDto | AssetItemDto
): asset is AWAssetDto {
  return 'decision' in asset;
}

const NAVIGATION_THROTTLE_MS = 200;

function canOpenAsset(asset: AWAssetDto | AssetItemDto) {
  return !isApprovalAsset(asset) || !asset.accessDisabled;
}

type AssetsSliderSource = 'share-review' | 'approval' | 'location';

export type AssetsSliderLoadMore = {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  isFetching: boolean;
  fetchNextPage: () => void;
  fetchPreviousPage: () => void;
};

/**
 * Slider list and navigation state. Use it only through AssetsSliderProvider,
 * so queries are observed once and survive remounts of the slider itself.
 */
export function useAssetsSliderNavigationState() {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const shareReview = useShareReview(true);
  const { password } = useApprovalPassword();
  const [searchParams] = useSearchParams();
  const selectedAsset = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.assets
  );
  const anchor = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.assetsSliderAnchor
  );
  const isInternalMediaViewer = location.pathname.includes('media-viewer');
  const isShareReviewLink = location.pathname.includes('external-reviewer');
  const isApprovalSource = useIsApprovalMediaViewerMode();
  let source: AssetsSliderSource = 'location';
  if (isShareReviewLink) source = 'share-review';
  else if (!isInternalMediaViewer || isApprovalSource) source = 'approval';
  const isLocationSource = source === 'location';

  const awId =
    searchParams.get('id') ?? selectedAsset?.asset?.approvalWorkflow?.id;
  const selectedAssetId = selectedAsset?.id;

  // Stepping (e.g. holding a hotkey) moves the slider selection right away,
  // while opening the asset is throttled. Until the latest step is opened,
  // the slider shows the pending asset and further steps continue from it.
  const [pendingAssetId, setPendingAssetId] = useState<string | null>(null);
  const currentAssetIdRef = useRef<string | null>(null);
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const lastNavigationAtRef = useRef(0);
  useEffect(() => {
    // Assets opened in between while stepping must not reset the selection
    if (!selectedAssetId || navigationTimeoutRef.current) return;
    currentAssetIdRef.current = selectedAssetId;
    setPendingAssetId(null);
  }, [selectedAssetId]);
  useEffect(
    () => () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    },
    []
  );
  // Position (in the whole list) selected on a skeleton, i.e. an entry whose
  // page is not loaded yet. Resolved to the asset once the page arrives.
  const [skeletonPosition, setSkeletonPosition] = useState<number | null>(null);
  // Navigating clears the selected asset until the next one loads
  const activeAssetId =
    skeletonPosition !== null
      ? undefined
      : pendingAssetId ??
        selectedAssetId ??
        currentAssetIdRef.current ??
        undefined;
  const { data: resApprovalWorkflow } = useGetApprovalWorkflow({
    enabled: source === 'approval',
    params: {
      id: awId || '',
      password
    }
  });
  const aw =
    source === 'approval' ? resApprovalWorkflow?.approvalWorkflow : null;

  // Assets of the folder (or campaign root) the anchor asset is in.
  // Params do not depend on the anchor asset, so re-anchoring keeps the list.
  const anchorWorkspaceId = anchor?.workspaceId;
  const anchorCampaignId = anchor?.campaignId;
  const anchorParentId = anchor?.parentId ?? null;
  const anchorOrderBy = anchor?.orderBy;
  const locationListParams = useMemo(
    () =>
      anchorWorkspaceId && anchorCampaignId
        ? {
            workspaceId: anchorWorkspaceId,
            campaignId: anchorCampaignId,
            parentId: anchorParentId,
            isFolder: false
          }
        : null,
    [anchorWorkspaceId, anchorCampaignId, anchorParentId]
  );
  // Slider scroll position, kept here as the slider is remounted per asset
  const scrollLeftRef = useRef<number | null>(null);
  // Mobile expanded state, kept here as the slider is remounted per asset
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);
  useEffect(() => {
    scrollLeftRef.current = null;
    setSkeletonPosition(null);
  }, [locationListParams, source]);

  const locationSliderParams = useMemo(
    () =>
      locationListParams && anchorOrderBy
        ? { ...locationListParams, orderBy: [anchorOrderBy] }
        : null,
    [locationListParams, anchorOrderBy]
  );
  const locationSlider = useAssetsSliderQuery(
    locationSliderParams ?? { workspaceId: '' },
    anchor?.assetId ?? null,
    { enabled: isLocationSource && !!locationSliderParams }
  );
  const { data: locationCount } = useAssetsCountQuery(
    locationListParams ?? { workspaceId: '' },
    { enabled: isLocationSource && !!locationListParams }
  );

  useEffect(() => {
    if (!isLocationSource || !selectedAsset || selectedAsset.isFolder) return;
    const parentId = selectedAsset.path.slice(-1)[0]?.id ?? null;
    const isSameList =
      !!anchor &&
      anchor.campaignId === selectedAsset.campaignId &&
      anchor.parentId === parentId;
    // Keep the list stable while navigating within it
    if (
      isSameList &&
      (anchor.assetId === selectedAsset.id ||
        locationSlider.isFetching ||
        locationSlider.items.some((x) => x.id === selectedAsset.id))
    ) {
      return;
    }
    dispatch(
      setAssetsSliderAnchor({
        assetId: selectedAsset.id,
        workspaceId: selectedAsset.workspaceId,
        campaignId: selectedAsset.campaignId,
        parentId,
        orderBy: isSameList ? anchor.orderBy : getInitialAssetsOrderByParam()
      })
    );
  }, [
    isLocationSource,
    selectedAsset,
    anchor,
    locationSlider.isFetching,
    locationSlider.items,
    dispatch
  ]);

  // Approval assets are sorted with the ones without a decision first, so a
  // decision would move the asset. Their order is kept while navigating
  // between assets, and is sorted again when this state is created anew (on
  // reload, or coming from or going to a list of the assets).
  const approvalOrderRef = useRef<{ awId: string; ids: string[] } | null>(null);
  const sortedAssets = useMemo((): (AWAssetDto | AssetItemDto)[] => {
    if (source === 'share-review') return shareReview?.content?.assets ?? [];
    if (source === 'location') return locationSlider.items;
    if (!aw?.assets) return [];
    // A copy: the sort is in place, the assets are the query's data
    const sorted = sortedAWListAssetsToReviewInBatch([...aw.assets]);
    const prevOrder = approvalOrderRef.current;
    const ordered =
      prevOrder?.awId === aw.id
        ? keepAssetsOrder(sorted, prevOrder.ids)
        : sorted;
    approvalOrderRef.current = {
      awId: aw.id,
      ids: ordered.map((item) => item.id)
    };
    return ordered;
  }, [
    source,
    shareReview?.content?.assets,
    locationSlider.items,
    aw?.id,
    aw?.assets
  ]);

  const count = isLocationSource
    ? Math.max(locationCount ?? 0, sortedAssets.length)
    : sortedAssets.length;
  const currentAssetPosition = useMemo(() => {
    if (skeletonPosition !== null) return skeletonPosition + 1;
    const index = sortedAssets.findIndex((item) => item.id === activeAssetId);
    if (index < 0) return 0;
    return (isLocationSource ? locationSlider.firstItemIndex : 0) + index + 1;
  }, [
    sortedAssets,
    activeAssetId,
    skeletonPosition,
    isLocationSource,
    locationSlider.firstItemIndex
  ]);

  // The selected asset's page is still loading (initially or after
  // re-anchoring), or entries before it are, which would shift it once shown
  const isSelectedAssetPending =
    isLocationSource &&
    !!selectedAssetId &&
    ((locationSlider.isFetching &&
      !sortedAssets.some((item) => item.id === selectedAssetId)) ||
      locationSlider.isAwaitingPreviousPage);

  // Entries around the loaded ones whose pages are not loaded yet, shown as
  // skeletons (up to a page) that can already be stepped onto
  const loadedStartIndex = isLocationSource ? locationSlider.firstItemIndex : 0;
  const loadedEndIndex = loadedStartIndex + sortedAssets.length;
  const leadingSkeletonCount =
    isLocationSource && locationSlider.hasPreviousPage
      ? Math.min(Math.max(loadedStartIndex, 1), ASSETS_SLIDER_PAGE_SIZE)
      : 0;
  const trailingSkeletonCount =
    isLocationSource && locationSlider.hasNextPage
      ? Math.min(Math.max(count - loadedEndIndex, 1), ASSETS_SLIDER_PAGE_SIZE)
      : 0;

  const loadMore: AssetsSliderLoadMore | null = isLocationSource
    ? {
        hasNextPage: locationSlider.hasNextPage,
        hasPreviousPage: locationSlider.hasPreviousPage,
        isFetching: locationSlider.isFetching,
        fetchNextPage: locationSlider.fetchNextPage,
        fetchPreviousPage: locationSlider.fetchPreviousPage
      }
    : null;

  const openAsset = (nextAsset: AWAssetDto | AssetItemDto) => {
    if (isApprovalAsset(nextAsset)) {
      if (isInternalMediaViewer && nextAsset.hasAccess) {
        navigate(getApprovalMediaViewerUrl(nextAsset.id, nextAsset.versionId), {
          replace: true
        });
        return;
      }
      if (isInternalMediaViewer && !nextAsset.hasAccess) {
        navigate(`/approval?id=${awId}&assetId=${nextAsset.id}`, {
          replace: true
        });
        return;
      }
      searchParams.set('assetId', nextAsset.id);
      searchParams.delete('versionId');
      navigate({ search: searchParams.toString() }, { replace: true });
    } else if (isLocationSource) {
      const versionId = nextAsset.asset?.versions[0]?.id;
      if (!versionId) return;
      navigate(`/media-viewer/${nextAsset.id}/${versionId}`, {
        replace: true
      });
    } else {
      dispatch(setAsset(nextAsset));
      dispatch(setAssetVariant(nonNull(nextAsset.asset).versions[0].id));
    }
  };

  /**
   * Selects the asset in the slider and opens it. With `throttle`, opening is
   * limited to one per NAVIGATION_THROTTLE_MS, always ending on the latest.
   */
  const onChangeAsset = (
    nextAsset: AWAssetDto | AssetItemDto,
    { throttle = false }: { throttle?: boolean } = {}
  ) => {
    if (!nextAsset || !canOpenAsset(nextAsset)) return;
    setSkeletonPosition(null);
    currentAssetIdRef.current = nextAsset.id;
    setPendingAssetId(nextAsset.id);
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
      navigationTimeoutRef.current = null;
    }
    const wait = throttle
      ? NAVIGATION_THROTTLE_MS - (Date.now() - lastNavigationAtRef.current)
      : 0;
    if (wait <= 0) {
      lastNavigationAtRef.current = Date.now();
      openAsset(nextAsset);
      return;
    }
    navigationTimeoutRef.current = setTimeout(() => {
      navigationTimeoutRef.current = null;
      lastNavigationAtRef.current = Date.now();
      openAsset(nextAsset);
    }, wait);
  };

  // Selects the entry at a position in the whole list (location lists):
  // the asset if loaded, otherwise its skeleton while its page is fetched
  const selectPosition = (position: number) => {
    const asset =
      position >= loadedStartIndex
        ? sortedAssets[position - loadedStartIndex]
        : undefined;
    if (asset) {
      setSkeletonPosition(null);
      onChangeAsset(asset, { throttle: true });
      return;
    }
    setSkeletonPosition(position);
    if (position < loadedStartIndex) locationSlider.fetchPreviousPage();
    else locationSlider.fetchNextPage();
  };

  // Resolve a selected skeleton once its page is loaded, or keep loading
  // towards it when stepped further than the fetched page
  const selectPositionRef = useRef(selectPosition);
  selectPositionRef.current = selectPosition;
  useEffect(() => {
    if (skeletonPosition === null || locationSlider.isFetching) return;
    const isLoaded =
      skeletonPosition >= loadedStartIndex && skeletonPosition < loadedEndIndex;
    const canLoadMore =
      skeletonPosition < loadedStartIndex
        ? locationSlider.hasPreviousPage
        : locationSlider.hasNextPage;
    if (isLoaded || canLoadMore) {
      selectPositionRef.current(skeletonPosition);
      return;
    }
    // Fewer entries than expected: settle on the nearest loaded one
    selectPositionRef.current(
      skeletonPosition < loadedStartIndex
        ? loadedStartIndex
        : loadedEndIndex - 1
    );
  }, [
    skeletonPosition,
    loadedStartIndex,
    loadedEndIndex,
    locationSlider.isFetching,
    locationSlider.hasPreviousPage,
    locationSlider.hasNextPage
  ]);

  // Wrapping around to an end of the list that is not loaded: its asset is
  // fetched and opened, the slider then re-anchors on it
  const isWrappingRef = useRef(false);
  const wrapToUnloadedEdge = async (edge: 'first' | 'last') => {
    if (!locationSliderParams || isWrappingRef.current) return;
    isWrappingRef.current = true;
    const fromAssetId = currentAssetIdRef.current;
    try {
      const asset = await fetchAssetsListEdge(locationSliderParams, edge);
      // Not when navigated elsewhere meanwhile
      if (!asset || currentAssetIdRef.current !== fromAssetId) return;
      onChangeAsset(asset);
    } catch {
      // Stays on the current asset
    } finally {
      isWrappingRef.current = false;
    }
  };

  const step = (direction: 1 | -1) => {
    const currentId = currentAssetIdRef.current ?? selectedAssetId;
    const currentIndex = sortedAssets.findIndex(
      (item) => item.id === currentId
    );
    if (isLocationSource) {
      const currentPosition =
        skeletonPosition ??
        (currentIndex >= 0 ? loadedStartIndex + currentIndex : null);
      if (currentPosition === null) return;
      const firstPosition = loadedStartIndex - leadingSkeletonCount;
      const lastPosition = loadedEndIndex + trailingSkeletonCount - 1;
      let target = currentPosition + direction;
      if (target < firstPosition || target > lastPosition) {
        // Past the loaded entries, not the end of the list yet
        const isListEnd =
          direction > 0
            ? !locationSlider.hasNextPage
            : !locationSlider.hasPreviousPage;
        if (!isListEnd) return;
        // Wrap around: to the other end when loaded, or by fetching it
        const isOtherEndLoaded =
          direction > 0
            ? !locationSlider.hasPreviousPage
            : !locationSlider.hasNextPage;
        if (!isOtherEndLoaded) {
          wrapToUnloadedEdge(direction > 0 ? 'first' : 'last');
          return;
        }
        target = direction > 0 ? loadedStartIndex : loadedEndIndex - 1;
      }
      selectPosition(target);
      return;
    }
    // Review/approval lists are fully loaded, some entries may be disabled
    const after =
      direction > 0
        ? sortedAssets.slice(currentIndex + 1)
        : sortedAssets.slice(0, Math.max(currentIndex, 0)).reverse();
    const wrapped =
      direction > 0
        ? sortedAssets.slice(0, Math.max(currentIndex, 0))
        : sortedAssets.slice(currentIndex + 1).reverse();
    const nextAsset = after.find(canOpenAsset) ?? wrapped.find(canOpenAsset);
    if (nextAsset) onChangeAsset(nextAsset, { throttle: true });
  };

  const onPrev = () => step(-1);
  const onNext = () => step(1);

  return {
    aw,
    awId,
    isLocationSource,
    // Shows the review progress (not for share links)
    isApprovalList: source === 'approval',
    sortedAssets,
    count,
    currentAssetPosition,
    selectedAssetId,
    activeAssetId,
    skeletonPosition,
    loadedStartIndex,
    leadingSkeletonCount,
    trailingSkeletonCount,
    isSelectedAssetPending,
    loadMore,
    scrollLeftRef,
    isMobileExpanded,
    setIsMobileExpanded,
    onChangeAsset,
    onPrev,
    onNext
  };
}

/** "2 of 7", or "7 media" while the position is not known yet */
export function getAssetsPositionLabel(position: number, count: number) {
  return position > 0 ? `${position} of ${count}` : `${count} media`;
}

export function useAssetsSliderReview(aw?: ApprovalWorkflowItemDto | null) {
  const currentStage = useMemo(() => {
    if (!aw?.stages?.length) return null;
    return (
      aw.stages.find((stage) => stage.id === aw.currentStageId) || aw.stages[0]
    );
  }, [aw?.stages, aw?.currentStageId]);

  const currentMember = useMemo(
    () => currentStage?.members.find((member) => member.me),
    [currentStage?.members]
  );

  return useMemo(() => {
    if (!currentMember?.canSubmitDecision) {
      return {
        reviewPositionLabel: null as string | null,
        isReviewed: false
      };
    }

    const reviewableAssets =
      aw?.assets?.filter((asset) => !asset.accessDisabled) || [];
    const toReviewCount = reviewableAssets.length;
    const reviewCount = reviewableAssets.filter((asset) =>
      currentMember.decisions?.some(
        (x) => x.assetVersionId === asset.versionId && x.decision
      )
    ).length;
    const canReview = toReviewCount > 0;

    return {
      reviewPositionLabel: canReview
        ? `${reviewCount} / ${toReviewCount}`
        : null,
      isReviewed: canReview && reviewCount === toReviewCount
    };
  }, [aw?.assets, currentMember]);
}

/** Key of a rendered slider entry, to locate it in the row */
export function getAssetsSliderSkeletonKey(position: number) {
  return `skeleton-${position}`;
}

function findSliderElement(content: HTMLElement, key: string) {
  return content.querySelector<HTMLElement>(`[data-slider-key="${key}"]`);
}

export function useScrollToActiveAsset({
  contentRef,
  scrollLeftRef,
  sortedAssets,
  activeKey,
  enabled
}: {
  contentRef: RefObject<HTMLDivElement>;
  scrollLeftRef: MutableRefObject<number | null>;
  sortedAssets: (AWAssetDto | AssetItemDto)[];
  /** Active asset id, or skeleton key while a skeleton is selected */
  activeKey?: string;
  enabled: boolean;
}) {
  // The row is not rendered until there are items to show
  const hasItems = sortedAssets.length > 0;

  // The slider is remounted for every asset. Restore the previous position
  // first, so scrolling to the new asset animates from where it was.
  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content || !enabled) return;
    const scrollLeft = scrollLeftRef.current;
    if (scrollLeft !== null) {
      const { scrollBehavior } = content.style;
      content.style.scrollBehavior = 'auto';
      content.scrollLeft = scrollLeft;
      content.style.scrollBehavior = scrollBehavior;
    }
    const onScroll = () => {
      scrollLeftRef.current = content.scrollLeft;
    };
    content.addEventListener('scroll', onScroll);
    return () => content.removeEventListener('scroll', onScroll);
  }, [contentRef, scrollLeftRef, enabled, hasItems]);

  // Jump to the selected asset without animation when the slider opens or
  // its items first show up (initial load, re-anchoring). Remounts caused by
  // navigating between assets start from the restored position and animate.
  const instantScrollRef = useRef(false);
  const prevVisibilityRef = useRef<{
    enabled: boolean;
    hasItems: boolean;
  } | null>(null);

  useLayoutEffect(() => {
    const prev = prevVisibilityRef.current;
    prevVisibilityRef.current = { enabled, hasItems };
    if (!enabled || !hasItems) return;
    if (prev ? !prev.enabled || !prev.hasItems : scrollLeftRef.current === null)
      instantScrollRef.current = true;
  }, [enabled, hasItems, scrollLeftRef]);

  // Keep the selected asset centered while items load (including the page
  // it is on, or pages prepended before it), until the user scrolls the row
  const followSelectedRef = useRef(true);
  // Animate only when moving to another asset, not when items change around it
  const scrolledAssetIdRef = useRef<string | null>(null);

  useEffect(() => {
    followSelectedRef.current = true;
  }, [activeKey, enabled]);

  useEffect(() => {
    const content = contentRef.current;
    if (!content || !enabled) return;
    // Programmatic scrolls also emit `scroll`, so listen to user input instead
    const stopFollowing = () => {
      followSelectedRef.current = false;
    };
    const events = ['wheel', 'pointerdown', 'touchstart'] as const;
    events.forEach((event) =>
      content.addEventListener(event, stopFollowing, { passive: true })
    );
    return () =>
      events.forEach((event) =>
        content.removeEventListener(event, stopFollowing)
      );
  }, [contentRef, enabled, hasItems]);

  useEffect(() => {
    const content = contentRef.current;
    if (!content || !enabled || !followSelectedRef.current) return;
    const itemElement = activeKey && findSliderElement(content, activeKey);
    if (!itemElement) return;
    const isSameAsset = scrolledAssetIdRef.current === activeKey;
    scrolledAssetIdRef.current = activeKey ?? null;
    if (instantScrollRef.current || isSameAsset) {
      instantScrollRef.current = false;
      // Override the row's CSS smooth scrolling for this jump
      const { scrollBehavior } = content.style;
      content.style.scrollBehavior = 'auto';
      itemElement.scrollIntoView({ block: 'nearest', inline: 'center' });
      content.style.scrollBehavior = scrollBehavior;
      return;
    }
    itemElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  }, [contentRef, sortedAssets, activeKey, enabled]);
}

/**
 * Loads more slider items when close to either end (within a viewport, where
 * the skeletons are), and keeps the visible items in place when items are
 * prepended.
 */
export function useAssetsSliderEdgeLoading({
  contentRef,
  sortedAssets,
  loadMore,
  enabled
}: {
  contentRef: RefObject<HTMLDivElement>;
  sortedAssets: (AWAssetDto | AssetItemDto)[];
  loadMore: AssetsSliderLoadMore | null;
  enabled: boolean;
}) {
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;
  const sortedAssetsRef = useRef(sortedAssets);
  sortedAssetsRef.current = sortedAssets;
  // The row is not rendered until there are items to show
  const hasItems = sortedAssets.length > 0;

  const loadNearEdge = useCallback(() => {
    const content = contentRef.current;
    const { current } = loadMoreRef;
    if (!content || !enabled || !current || current.isFetching) return;
    // Measured from the loaded items, not the row's ends: up to a page of
    // skeletons follows them, which can be shown far from the row's end
    const assets = sortedAssetsRef.current;
    if (!assets.length) return;
    const firstElement = findSliderElement(content, assets[0].id);
    const lastElement = findSliderElement(
      content,
      assets[assets.length - 1].id
    );
    if (!firstElement || !lastElement) return;
    const contentRect = content.getBoundingClientRect();
    const threshold = content.clientWidth;
    if (
      current.hasNextPage &&
      lastElement.getBoundingClientRect().right < contentRect.right + threshold
    ) {
      current.fetchNextPage();
    } else if (
      current.hasPreviousPage &&
      firstElement.getBoundingClientRect().left > contentRect.left - threshold
    ) {
      current.fetchPreviousPage();
    }
  }, [contentRef, enabled]);

  useEffect(() => {
    const content = contentRef.current;
    if (!content || !enabled) return undefined;
    content.addEventListener('scroll', loadNearEdge);
    // E.g. a wider row showing the skeletons
    const resizeObserver = new ResizeObserver(loadNearEdge);
    resizeObserver.observe(content);
    return () => {
      content.removeEventListener('scroll', loadNearEdge);
      resizeObserver.disconnect();
    };
  }, [contentRef, enabled, hasItems, loadNearEdge]);

  // Not only on scroll: skeletons still in view once a page is loaded (or
  // shown without scrolling) load the next one
  const isFetching = !!loadMore?.isFetching;
  const hasNextPage = !!loadMore?.hasNextPage;
  const hasPreviousPage = !!loadMore?.hasPreviousPage;
  useEffect(() => {
    loadNearEdge();
  }, [sortedAssets, isFetching, hasNextPage, hasPreviousPage, loadNearEdge]);

  const firstItemRef = useRef<{ id: string; offsetLeft: number } | null>(null);

  useLayoutEffect(() => {
    const content = contentRef.current;
    const prevFirstItem = firstItemRef.current;
    if (content && enabled && prevFirstItem) {
      const element = findSliderElement(content, prevFirstItem.id);
      const delta = element ? element.offsetLeft - prevFirstItem.offsetLeft : 0;
      if (delta) {
        const { scrollBehavior } = content.style;
        content.style.scrollBehavior = 'auto';
        content.scrollLeft += delta;
        content.style.scrollBehavior = scrollBehavior;
      }
    }
    const firstId = sortedAssets[0]?.id;
    const firstElement =
      content && firstId ? findSliderElement(content, firstId) : null;
    firstItemRef.current =
      enabled && firstId && firstElement
        ? { id: firstId, offsetLeft: firstElement.offsetLeft }
        : null;
  }, [contentRef, sortedAssets, enabled]);
}
